/**
 * Language Dropdown
 * Simple dropdown menu for selecting language
 */

import React, { useContext, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TranslationContext } from '../../hooks/useTranslation';

interface LanguageDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ isOpen, onClose, anchorRef }) => {
  const context = useContext(TranslationContext);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside as any);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !context) return null;

  const { language, setLanguage, availableLanguages } = context;

  const handleSelect = (langCode: string) => {
    setLanguage(langCode);
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    onClose();
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {availableLanguages.map(lang => (
        <DropdownItem
          key={lang.code}
          $active={lang.code === language}
          onClick={() => handleSelect(lang.code)}
        >
          <FlagEmoji>{lang.flag}</FlagEmoji>
          <LangName>{lang.name}</LangName>
          {lang.code === language && (
            <CheckMark>✓</CheckMark>
          )}
        </DropdownItem>
      ))}
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: var(--tg-theme-bg-color, #2c2c2e);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  border: 1px solid var(--tg-theme-hint-color, #8e8e93)30;
`;

const DropdownItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  gap: 12px;
  background: ${props => props.$active
    ? 'var(--tg-theme-button-color, #3390ec)20'
    : 'transparent'};
  border: none;
  cursor: pointer;
  transition: background 0.15s;

  &:first-child {
    border-radius: 12px 12px 0 0;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }

  &:only-child {
    border-radius: 12px;
  }

  &:active {
    background: var(--tg-theme-hint-color, #8e8e93)20;
  }
`;

const FlagEmoji = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const LangName = styled.span`
  flex: 1;
  text-align: left;
  font-size: 15px;
  color: var(--tg-theme-text-color, #ffffff);
`;

const CheckMark = styled.span`
  color: var(--tg-theme-button-color, #3390ec);
  font-weight: 600;
  font-size: 16px;
`;

export default LanguageDropdown;
